import { CheckCircle2, Circle, AlertCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type CheckpointState = "pending" | "active" | "completed" | "failed";
export type LabelAlignment = "start" | "center" | "end";

interface ProgressCheckpointProps {
  state: CheckpointState;
  label: string;
  showLabel?: boolean;
  labelAlignment?: LabelAlignment;
}

export function ProgressCheckpoint({
  state,
  label,
  showLabel = true,
  labelAlignment = "center",
}: ProgressCheckpointProps) {
  return (
    <div className="relative z-10">
      {/* Background circle to hide progress bar behind */}
      <div className="absolute inset-0 bg-card rounded-full size-8" />

      {/* Animated outer ring for active state */}
      {state === "active" && (
        <>
          <span className="absolute inline-flex inset-[-2px] rounded-full bg-primary opacity-75 animate-ping" />
          <span className="absolute inline-flex inset-[-2px] rounded-full border-2 border-primary" />
        </>
      )}

      {/* Checkpoint Circle */}
      <div
        className={cn(
          "relative flex items-center justify-center size-8 rounded-full border-2 transition-all duration-300",
          {
            // Pending state
            "bg-muted border-muted-foreground/20": state === "pending",
            // Active or Completed state
            "bg-primary border-primary":
              state === "active" ||
              state === "completed" ||
              state === "failed",
          },
        )}
      >
        {/* Icons based on state - all white */}
        {state === "pending" && <Circle className="size-3 text-white" />}
        {state === "active" && (
          <Loader2 className="size-4 text-white animate-spin" />
        )}
        {state === "completed" && (
          <CheckCircle2 className="size-4 text-white" />
        )}
        {state === "failed" && <AlertCircle className="size-4 text-white" />}
      </div>

      {/* Label - absolutely positioned */}
      {showLabel && (
        <span
          className={cn(
            "absolute top-full mt-2 text-xs text-muted-foreground whitespace-nowrap",
            {
              "left-0": labelAlignment === "start",
              "left-1/2 -translate-x-1/2": labelAlignment === "center",
              "right-0": labelAlignment === "end",
            },
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
}
